import React, { useEffect, useMemo } from "react"
import { Form, InputNumber, Space, Typography, Spin, Flex } from "antd"
import { useSelector, useDispatch } from "react-redux"
import { debounce } from "lodash"
import { getAssetAllocation } from "store/bookings"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"
import PropTypes from "prop-types"
import styles from "./BookingForm.module.css"
const { Text } = Typography

const GuestInputGroup = ({ form, setCounts, disabled, booking }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    experienceBookingData,
    experienceBookingData: { bookingSelections },
    experienceById: { experienceDetail }
  } = useSelector((state) => state.experiences)
  const assetAllocation = useSelector((state) => state.bookings.assetAllocation)
  const { children_allowed, infants_allowed, passengers_allowed } = bookingSelections?.tripLength?.price || {}
  const maxGuests = experienceBookingData?.bookingData?.max_guests
  const minGuests = experienceBookingData?.bookingData?.min_guests

  const maxGuestsLimit = maxGuests || 0
  const experienceId = experienceDetail?.id || bookingSelections?.experience?.id

  // Create debounced function for fetching asset allocation
  const debouncedFetchAssets = useMemo(
    () =>
      debounce((counts, tripId, date, timeSlot, maxGuests, minGuests) => {
        const mainCounts = counts.main || {}
        const extraCounts = counts.extra || {}

        const adultCount = (mainCounts.adults || 0) + (extraCounts.adults || 0)
        const childCount = (mainCounts.children || 0) + (extraCounts.children || 0)
        const infantCount = (mainCounts.infants || 0) + (extraCounts.infants || 0)
        const passengerCount = (mainCounts.passengers || 0) + (extraCounts.passengers || 0)

        // Calculate totals for validation
        const totalMainGuests = Object.values(mainCounts).reduce((sum, val) => sum + (val || 0), 0)
        const totalGuests = adultCount + childCount + infantCount + passengerCount

        // Don't call API if:
        // 1. Any required field is empty
        // 2. All guest counts are 0
        // 3. Validation fails (exceeds limits)
        if (!tripId || !date || !timeSlot || totalGuests === 0) {
          return
        }

        // Validate main guest count
        if (totalMainGuests > maxGuests || totalMainGuests < minGuests) {
          return
        }

        const params = {
          trip_id: tripId,
          date: date,
          time: timeSlot,
          adult_count: adultCount,
          child_count: childCount,
          infant_count: infantCount,
          passenger_count: passengerCount
        }

        // Only call API if NEW_RESOURCES feature is enabled
        if (isFeatureEnabled('NEW_RESOURCES')) {
          dispatch(getAssetAllocation(params))
        }
      }, 500),
    [dispatch]
  )

  const createValidator = (fieldName, sectionKey) => (_, value) => {
    const values = form.getFieldsValue([sectionKey])
    const sectionValues = values[sectionKey] || {}
    sectionValues[fieldName] = value
    const total = Object.values(sectionValues).reduce((sum, v) => sum + (Number(v) || 0), 0)

    if (fieldName === "adults" && (!value || value < 1) && sectionKey === "main") {
      return Promise.reject(new Error(t("booking.error_messages.adults_required")))
    }

    if (value > 0) {
      if (total > maxGuestsLimit && sectionKey === "main") {
        return Promise.reject(new Error(t("booking.error_messages.max_guest_limit", { max: maxGuestsLimit })))
      } else if (total < minGuests && sectionKey === "main") {
        return Promise.reject(new Error(t("booking.error_messages.min_guest_limit", { min: minGuests })))
      }
    }
    return Promise.resolve()
  }

  const updateCount = (section, key, value) => {
    let newCounts = {
      ...bookingSelections?.counts,
      [section]: {
        ...bookingSelections?.counts?.[section],
        [key]: value
      }
    }
    // Logic to clear extra counts if main changes is no longer needed as we only use main

    setCounts(newCounts)
  }

  useEffect(() => {
    const counts = bookingSelections?.counts
    if (counts) {
      form.setFieldsValue({
        main: counts?.main
      })
    }
  }, [bookingSelections?.counts, form])

  useEffect(() => {
    // Only set if minGuests is defined and main.adults is not already set
    if (minGuests > 0) {
      const currentMain = form.getFieldValue(["main", "adults"])
      if (!currentMain) {
        form.setFieldsValue({ main: { ...form.getFieldValue("main"), adults: minGuests } })
        // Optionally, update counts state if needed
        setCounts({
          ...bookingSelections?.counts,
          main: { ...bookingSelections?.counts?.main, adults: booking?.adult_count || minGuests }
        })
      }
    }
    // Only run when minGuests changes
    // eslint-disable-next-line
  }, [minGuests])

  useEffect(() => {
    // Only fetch if there are valid guest counts and no validation errors
    const counts = bookingSelections?.counts
    const date = bookingSelections?.date
    const timeSlot = bookingSelections?.timeSlot

    if (counts && experienceId && date && timeSlot) {
      const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)

      // Additional validation: check guest count limits
      const mainCounts = counts.main || {}
      const totalMainGuests = Object.values(mainCounts).reduce((sum, val) => sum + (val || 0), 0)

      // Don't call API if there are form errors or validation fails
      const isMainGuestValid = totalMainGuests >= minGuests && totalMainGuests <= maxGuests

      if (!hasErrors && isMainGuestValid) {
        debouncedFetchAssets(counts, experienceId, date, timeSlot, maxGuests, minGuests)
      }
    }

    // Cleanup function to cancel pending debounced calls on unmount
    return () => {
      debouncedFetchAssets.cancel()
    }
    // eslint-disable-next-line
  }, [
    bookingSelections?.counts,
    experienceId,
    bookingSelections?.date,
    bookingSelections?.timeSlot,
    debouncedFetchAssets,
    maxGuests,
    minGuests
  ])

  const renderGuestInputBlock = (sectionLabel, sectionKey) => (
    <>
      {sectionLabel && (
        <div className="mb-sm">
          <Text className={styles.sectionLabel}>{sectionLabel}</Text>
        </div>
      )}
      <Space size="small" className={styles.inputGroup}>
        {[
          { label: t("booking.labels.adults"), key: "adults", allowed: true },
          { label: t("booking.labels.children"), key: "children", allowed: children_allowed },
          { label: t("booking.labels.infants"), key: "infants", allowed: infants_allowed },
          { label: t("booking.labels.passengers"), key: "passengers", allowed: passengers_allowed }
        ].map(({ label, key, allowed }) => (
          <Form.Item
            key={sectionKey + key}
            label={label}
            name={[sectionKey, key]} // nested form field path
            rules={[{ validator: createValidator(key, sectionKey) }]}
          >
            <InputNumber
              min={0}
              disabled={!allowed || disabled}
              className={styles.inputNumber}
              onChange={(val) => updateCount(sectionKey, key, val)}
            />
          </Form.Item>
        ))}
      </Space>
    </>
  )

  return (
    <>
      {renderGuestInputBlock("", "main")}
      {assetAllocation.loading && (
        <Flex align="center" gap={8} className="py-xs">
          <Spin size="small" />
          <Text>Calculating asset allocation...</Text>
        </Flex>
      )}
      {assetAllocation.count !== null && !assetAllocation.loading && (
        <Flex className="py-xs">
          <Text>
            {assetAllocation.count} {assetAllocation.count === 1 ? "asset" : "assets"} will be allocated for this booking based on selected guests and asset capacity
          </Text>
        </Flex>
      )}
    </>
  )
}

GuestInputGroup.propTypes = {
  form: PropTypes.object.isRequired,
  setCounts: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  booking: PropTypes.object.isRequired
}

export default GuestInputGroup
